/**
 * RequirePractitioner — Route guard for /practitioner/* routes.
 *
 * Mirror of RequireAdmin, enforcing:
 *   1. Session must exist (else → /admin/login?next=<current>)
 *   2. super_admin browsing /practitioner/* is bounced to /admin/clinical
 *      because super_admins access clinical data through /admin/clinical,
 *      NOT through the practitioner portal (role-isolation rule from
 *      the routing rename spec).
 *   3. Anyone whose role is not practitioner_lite / practitioner_pro AND
 *      not super_admin (defensive) → /admin/login.
 *
 * The backend subscription gate middleware still runs on every /api/*
 * call and will redirect blocked practitioners to /subscribe via a
 * 403 { code: "subscription_required" } response — that path is
 * unaffected by this guard.
 */
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { adminAuth } from "@/lib/api";

const PRACTITIONER_ROLES = new Set(["practitioner_lite", "practitioner_pro"]);

export default function RequirePractitioner({ children }) {
  const location = useLocation();
  const token = adminAuth.getToken();
  if (!token) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace data-testid="require-practitioner-redirect" />;
  }
  const role = adminAuth.getRole?.() || "";
  // ── HARD ROLE ISOLATION — super_admin browses /admin/clinical, not here ──
  if (role === "super_admin") {
    return <Navigate to="/admin/clinical" replace data-testid="require-practitioner-super-block" />;
  }
  if (!PRACTITIONER_ROLES.has(role)) {
    return <Navigate to="/login" replace data-testid="require-practitioner-unknown-role" />;
  }
  return children;
}
